/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { AppConfig } from '@/types/config';

/** Social platforms the storefront ships an icon for. */
export type SocialPlatform = 'instagram' | 'facebook' | 'youtube' | 'x';

/** Host patterns per platform. Matches the bare host and any subdomain (`www.`, `m.`, …). */
const PLATFORM_HOSTS: ReadonlyArray<readonly [SocialPlatform, RegExp]> = [
    ['instagram', /(?:^|\.)instagram\.com$/i],
    ['facebook', /(?:^|\.)(?:facebook\.com|fb\.com)$/i],
    ['youtube', /(?:^|\.)(?:youtube\.com|youtu\.be)$/i],
    ['x', /(?:^|\.)(?:x\.com|twitter\.com)$/i],
];

/** Identify which social platform a profile URL points at, or `undefined` if it isn't one we render. */
export function platformForUrl(url: string): SocialPlatform | undefined {
    try {
        const { hostname } = new URL(url.trim());
        return PLATFORM_HOSTS.find(([, pattern]) => pattern.test(hostname))?.[0];
    } catch {
        // Not an absolute URL — nothing to match against.
        return undefined;
    }
}

/**
 * Resolve the brand's social profile URLs from `config.global.seo.organization.sameAs` (UCR-3004).
 *
 * `sameAs` is the single source of truth: the same list feeds the Organization JSON-LD and the UI
 * social rows, so the structured data can't drift from the links a shopper actually sees. Platforms
 * absent from `sameAs` are simply not rendered — no placeholder or third-party fallback. When the
 * same platform appears twice, the first entry wins.
 */
export function resolveSocialLinks(config: AppConfig): Partial<Record<SocialPlatform, string>> {
    const links: Partial<Record<SocialPlatform, string>> = {};
    for (const entry of config.global?.seo?.organization?.sameAs ?? []) {
        const url = entry.trim();
        const platform = platformForUrl(url);
        if (platform && !links[platform]) links[platform] = url;
    }
    return links;
}
